import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    signInWithPopup,
    signOut,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../../../config/firebase';
import { message } from 'antd';

const AuthContext = createContext();

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const signInWithGoogle = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const user = result.user;

            await setDoc(doc(db, 'users', user.uid), {
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL,
                createdAt: new Date(),
                lastLogin: new Date()
            }, { merge: true });

            message.success('Đăng nhập thành công!');
            return user;
        } catch (error) {
            message.error('Lỗi đăng nhập: ' + error.message);
            throw error;
        }
    };

    const signInWithEmail = async (email, password) => {
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            await setDoc(doc(db, 'users', result.user.uid), {
                lastLogin: new Date()
            }, { merge: true });
            message.success('Đăng nhập thành công!');
            return result.user;
        } catch (error) {
            message.error('Lỗi đăng nhập: ' + error.message);
            throw error;
        }
    };

    const signUpWithEmail = async (email, password, displayName) => {
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);
            await setDoc(doc(db, 'users', result.user.uid), {
                uid: result.user.uid,
                email: result.user.email,
                displayName: displayName,
                createdAt: new Date(),
                lastLogin: new Date()
            });
            message.success('Đăng ký thành công!');
            return result.user;
        } catch (error) {
            message.error('Lỗi đăng ký: ' + error.message);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
            message.success('Đăng xuất thành công!');
        } catch (error) {
            message.error('Lỗi đăng xuất: ' + error.message);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
